import _filter from 'lodash/filter';
import _findIndex from 'lodash/findIndex';
import _includes from 'lodash/includes';
import _keys from 'lodash/keys';
import _map from 'lodash/map';
import _mapValues from 'lodash/mapValues';
import _values from 'lodash/values';
import {
  AnalyticsEventType,
  AnalyticsFilterItem,
  DimensionValue,
  MetricValue,
} from '../analytics/analytics.types';
import extractCustomConversionsFromMetrics from '../analytics/utils/extractCustomConversionsFromMetrics';
import {DEFAULT_WIDGET_TYPE, WIDGET_ORIGIN_SIZE} from './dashboards.constants';
import {
  Dashboard,
  DashboardWidget,
  DashboardWidgetOption,
  DashboardWidgetSize,
  DashboardWidgetType,
  RemoteDashboard,
  RemoteDashboardWidget,
  RemoteDashboardWidgetOption,
  RemoteFilter,
} from './dashboards.types';

function toClientDashboard(dashboard: RemoteDashboard): Dashboard {
  return {
    id: dashboard.Id || dashboard.id,
    name: dashboard.Name || dashboard.name,
    isDefault: dashboard.IsDefault || dashboard.isDefault,
    readOnly: dashboard.ReadOnly || dashboard.readOnly,
  };
}

function toClientFilter(filters: RemoteFilter[]): AnalyticsFilterItem[] {
  return _map(filters, ({dimension, filtersValues}) => {
    return {
      allValues: filtersValues,
      filterBy: dimension,
    };
  });
}

function toServerFilter(filters: AnalyticsFilterItem[]): RemoteFilter[] {
  return _map(filters, ({filterBy, allValues}) => {
    return {
      filtersValues: allValues,
      dimension: filterBy,
    };
  }) as RemoteFilter[];
}

function toClientDashboardWidget(
  widget: RemoteDashboardWidget,
  childWidgets?: RemoteDashboardWidget[],
): DashboardWidget {
  const childrenForCurrentWidget = _filter(childWidgets, ({config}) =>
    _includes(config, String(widget.id)),
  );

  return {
    id: widget.id,
    name: widget.name,
    dashboardId: widget.dashboardId,
    type: widget.type as DashboardWidgetType,
    dimensions: (widget.dimensions || []) as DimensionValue[],
    metrics: [...(widget.metrics ?? []), ...(widget.customConversions ?? [])] as MetricValue[],
    filters: toClientFilter(widget.filters),
    config: widget.config || [],
    breakdownByTime: widget.breakdownByTime,
    layout: {
      i: String(widget.id),
      x: widget.xCoordinate,
      y: widget.yCoordinate,
      w: widget.width,
      h: widget.height,
    },
    children: _map([...childrenForCurrentWidget], (child) => toClientDashboardWidget(child)),
    showTrends: widget.showTrends,
    eventType: widget.eventType ?? AnalyticsEventType.Adserving,
  };
}

function toServerDashboardWidget(widget: DashboardWidget): RemoteDashboardWidget {
  const [customConversions, metrics] = extractCustomConversionsFromMetrics(widget.metrics);

  return {
    id: widget.id || 0,
    dashboardId: widget.dashboardId ?? 0,
    name: widget.name,
    type: widget.type,
    height: widget.layout.h,
    width: widget.layout.w,
    dimensions: widget.dimensions,
    metrics,
    customConversions,
    filters: toServerFilter(widget.filters),
    xCoordinate: widget.layout.x,
    yCoordinate: widget.layout.y,
    config: widget.config,
    breakdownByTime: widget.breakdownByTime,
    showTrends: widget.showTrends,
    eventType: widget.eventType ?? AnalyticsEventType.Adserving,
  };
}

function toClientWidgetsOptions(
  options: Record<DashboardWidgetType, RemoteDashboardWidgetOption>,
): Record<DashboardWidgetType, DashboardWidgetOption> {
  const widgetsOptions = _mapValues(options, ({maxDims, maxMetrics, sizes}, type) => {
    return {
      maxDimensions: maxDims,
      maxMetrics: type === DashboardWidgetType.HORIZONTAL_BAR ? 1 : maxMetrics,
      sizes: _map(sizes, ({width, height}) => {
        const size = _keys(WIDGET_ORIGIN_SIZE)[
          _findIndex(_values(WIDGET_ORIGIN_SIZE), {
            width,
            height,
          })
        ] as DashboardWidgetSize;

        return size;
      }),
    };
  });

  return {
    ...widgetsOptions,
    ...DEFAULT_WIDGET_TYPE,
  };
}

export {
  toClientDashboard,
  toClientDashboardWidget,
  toClientWidgetsOptions,
  toServerDashboardWidget,
};
